import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FaTrash, FaFlask, FaChevronUp, FaEdit, FaChalkboardTeacher, FaExclamationTriangle, FaUsers, FaClock } from 'react-icons/fa';
import { MdMeetingRoom } from 'react-icons/md';
import { Select, Badge, Spin, Dropdown, Menu } from 'antd';
import api from '../../../../api';

const animationStyles = `
  @keyframes slideInFade {
    0% { opacity: 0; transform: translateY(5px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  .animate-faculty-reveal {
    animation: slideInFade 0.4s ease-out forwards;
  }
  .animate-pulse-slow {
    animation: pulse 1.5s ease-in-out infinite;
  }
`;

function to12Hour(time24) {
  if (!time24) return '';
  let [h, m] = time24.split(':');
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${m} ${ampm}`;
}

// Common break options - only for name, not time
const COMMON_BREAKS = [
  { label: 'Short Break', icon: '' },
  { label: 'Lunch Break', icon: '' },
  { label: 'Water Break', icon: '' },
  { label: 'Restroom Break', icon: '' },
  { label: 'Movement Break', icon: '' },
  { label: 'Reading Break', icon: '' },
  { label: 'Stretch Break', icon: '' },
  { label: 'Snack Break', icon: '' }
];

// Faculty name cache
const facultyNameCache = new Map();
const pendingFacultyRequests = new Map();

const fetchFacultyName = async (facultyId, token) => {
  if (!facultyId) return null;
  const idStr = String(facultyId);
  
  if (facultyNameCache.has(idStr)) {
    return facultyNameCache.get(idStr);
  }
  
  if (pendingFacultyRequests.has(idStr)) {
    return pendingFacultyRequests.get(idStr);
  }
  
  const promise = (async () => {
    try {
      const response = await api.get(`/api/users/${facultyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = response.data;
      let name = user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
      if (!name) name = user?.email || facultyId;
      facultyNameCache.set(idStr, name);
      return name;
    } catch (err) {
      console.error('[TimetableEntry] Failed to fetch faculty name:', err);
      facultyNameCache.set(idStr, facultyId);
      return facultyId;
    } finally {
      pendingFacultyRequests.delete(idStr);
    }
  })();
  
  pendingFacultyRequests.set(idStr, promise);
  return promise;
};

const TimetableEntry = (props) => {
  const {
    entry,
    index,
    subjects,
    labBatches,
    onUpdate,
    onRemove,
    onSubjectChange,
    electiveGroups: electiveGroupsProp = [],
    disableEditing = false,
    tenantId: propTenantId,
    academicYearId: propAcademicYearId,
    selectedAcademicYearId,
    isSemesterBased,
    semesterId,
    classId,
    selectedTermId,
    token
  } = props;

  const [isExpanded, setIsExpanded] = useState(!entry.subjectId && !entry.electiveGroupId && !entry.label);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [groupFacultyNames, setGroupFacultyNames] = useState({});
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [currentConflict, setCurrentConflict] = useState(null);
  const [conflictCheckDebounce, setConflictCheckDebounce] = useState(null);
  const [loadingGroupData, setLoadingGroupData] = useState(false);

  const safeSubjects = useMemo(() => Array.isArray(subjects) ? subjects : [], [subjects]);
  const safeLabBatches = useMemo(() => Array.isArray(labBatches) ? labBatches : [], [labBatches]);
  const effectiveElectiveGroups = useMemo(() => Array.isArray(electiveGroupsProp) ? electiveGroupsProp : [], [electiveGroupsProp]);

  const tenantId = propTenantId || localStorage.getItem('tenantId');
  const academicYearId = propAcademicYearId || selectedAcademicYearId || localStorage.getItem('academicYearId');
  const authToken = token || localStorage.getItem('token');

  const selectedElectiveGroup = useMemo(() => 
    effectiveElectiveGroups.find(g => String(g._id) === String(entry.electiveGroupId)),
    [effectiveElectiveGroups, entry.electiveGroupId]
  );

  // Create a set of subject IDs that are part of ANY group
  const subjectIdsInGroups = useMemo(() => {
    const ids = new Set();
    effectiveElectiveGroups.forEach(group => {
      (group.subjects || []).forEach(gs => {
        if (gs.subjectId) {
          ids.add(String(gs.subjectId));
        }
      });
    });
    return ids;
  }, [effectiveElectiveGroups]);

  // Build dropdown options - simplified to prevent freezing
  const dropdownOptions = useMemo(() => {
    const regularOptions = [];
    const groupOptions = [];
    
    // Regular Subjects - ONLY those NOT in any group
    const regularSubjects = safeSubjects.filter(sub => {
      const subId = String(sub._id || sub.id);
      return !subjectIdsInGroups.has(subId);
    });
    
    regularSubjects.forEach(sub => {
      const subId = sub._id || sub.id;
      const subjectName = sub.name || sub.subjectName;
      const subjectCode = sub.subjectCode || sub.code;
      const displayName = subjectCode ? `${subjectName} [${subjectCode}]` : subjectName;
      
      regularOptions.push({
        value: subId,
        label: displayName,
        searchText: `${subjectName} ${subjectCode || ''}`.toLowerCase(),
        type: 'subject',
        isLab: sub.isLab || sub.type === 'Lab',
        subjectCode: subjectCode
      });
    });
    
    // Groups
    effectiveElectiveGroups.forEach(group => {
      const groupName = group.groupName || group.name;
      const groupLabel = group.label;
      const subjectsList = group.subjects || [];
      const subjectNames = subjectsList.map(s => {
        const sub = safeSubjects.find(sb => String(sb._id || sb.id) === String(s.subjectId));
        const hasSubgroups = s.subGroups && s.subGroups.length > 0;
        return `${sub?.name || 'Unknown'}${hasSubgroups ? ` (${s.subGroups.length})` : ''}`;
      }).join(', ');
      
      const displayName = groupLabel ? `${groupName} (${groupLabel})` : groupName;
      const searchText = `${groupName} ${groupLabel || ''} ${subjectNames}`.toLowerCase();
      
      groupOptions.push({
        value: `group:${group._id}`,
        label: displayName,
        searchText: searchText,
        description: subjectNames,
        subjects: subjectsList
      });
    });
    
    return { regularOptions, groupOptions };
  }, [safeSubjects, effectiveElectiveGroups, subjectIdsInGroups]);

  // Get faculty names for group subjects efficiently
  useEffect(() => {
    const fetchGroupFacultyNames = async () => {
      if (!selectedElectiveGroup) {
        setGroupFacultyNames({});
        return;
      }
      
      setLoadingGroupData(true);
      const names = {};
      const facultyIds = new Set();
      
      for (const gs of selectedElectiveGroup.subjects || []) {
        if (gs.facultyId) facultyIds.add(String(gs.facultyId));
        if (gs.subGroups && gs.subGroups.length > 0) {
          for (const sg of gs.subGroups) {
            if (sg.facultyId) facultyIds.add(String(sg.facultyId));
          }
        }
      }
      
      const fetchPromises = Array.from(facultyIds).map(async (fid) => {
        const name = await fetchFacultyName(fid, authToken);
        return { fid, name };
      });
      
      const results = await Promise.all(fetchPromises);
      results.forEach(({ fid, name }) => {
        names[fid] = name;
      });
      
      for (const gs of selectedElectiveGroup.subjects || []) {
        if (gs.facultyId) {
          names[gs.subjectId] = names[gs.facultyId] || 'No faculty available';
        }
        if (gs.subGroups && gs.subGroups.length > 0) {
          for (const sg of gs.subGroups) {
            if (sg.facultyId) {
              names[`${gs.subjectId}_${sg.subGroupId}`] = names[sg.facultyId] || 'No faculty available';
            }
          }
        }
      }
      
      setGroupFacultyNames(names);
      setLoadingGroupData(false);
    };
    
    fetchGroupFacultyNames();
  }, [selectedElectiveGroup, authToken]);

  const [selectedSubjectFaculty, setSelectedSubjectFaculty] = useState(null);

  // DEBUG: Log entry changes (especially batchId)
  useEffect(() => {
    console.log('[TimetableEntry] entry changed:', entry);
  }, [entry]);

  // Fetch faculty for selected subject (regular or lab)
  useEffect(() => {
    const loadFaculty = async () => {
      // Lab subject: wait for both subjectId and batchId
      const subject = safeSubjects.find(s => String(s.id || s._id) === String(entry.subjectId));
      const isLab = subject?.isLab || subject?.type === 'Lab' || subject?.subjectType === 'Lab';
      console.log('[TimetableEntry] loadFaculty effect:', {
        subjectId: entry.subjectId,
        batchId: entry.batchId,
        isLab,
        electiveGroupId: entry.electiveGroupId
      });
      if (isLab && entry.subjectId && entry.batchId && !entry.electiveGroupId) {
        // Fetch from backend for lab batch/subject
        setSelectedSubjectFaculty('');
        try {
          console.log('[TimetableEntry] Fetching lab batch faculty:', `/api/classManager/lab-batches/${entry.batchId}/subjects/${entry.subjectId}/faculty`);
          const res = await api.get(`/api/classManager/lab-batches/${entry.batchId}/subjects/${entry.subjectId}/faculty`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          console.log('[TimetableEntry] API response:', res.data);
          console.log('[TimetableEntry] Faculty array:', res.data.faculty);
          if (res.data.faculty && res.data.faculty.length > 0) {
            // Use fetchFacultyName for each facultyId to resolve name as for regular subjects
            const facultyNames = await Promise.all(
              res.data.faculty.map(f => fetchFacultyName(f.facultyId, authToken))
            );
            setSelectedSubjectFaculty(facultyNames.join(', '));
          } else {
            setSelectedSubjectFaculty('No faculty assigned');
          }
        } catch (err) {
          console.error('[TimetableEntry] Error fetching lab batch faculty:', err);
          setSelectedSubjectFaculty('Error fetching faculty');
        }
        return;
      }
      // Regular subject
      if (entry.subjectId && !entry.electiveGroupId && !isLab) {
        if (subject?.facultyAssignments?.length) {
          const facultyAssignment = subject.facultyAssignments.find(f => 
            f.classAssignments?.some(ca => String(ca.classId) === String(classId))
          );
          const faculty = facultyAssignment || subject.facultyAssignments[0];
          if (faculty?.facultyId) {
            const name = await fetchFacultyName(faculty.facultyId, authToken);
            setSelectedSubjectFaculty(name);
            return;
          }
        }
        setSelectedSubjectFaculty(subject?.facultyName || subject?.faculty || null);
      } else {
        setSelectedSubjectFaculty(null);
      }
    };
    loadFaculty();
  }, [entry.subjectId, entry.batchId, entry.electiveGroupId, safeSubjects, classId, authToken]);

  const checkEntryConflict = useCallback(async (subjectId, facultyIdsToCheck = null) => {
    if (!subjectId && !facultyIdsToCheck) return;
    
    if (conflictCheckDebounce) clearTimeout(conflictCheckDebounce);
    
    const timeoutId = setTimeout(async () => {
      setCheckingConflict(true);
      try {
        let facultyIds = facultyIdsToCheck;
        
        if (!facultyIds && subjectId) {
          const subject = safeSubjects.find(s => String(s.id || s._id) === String(subjectId));
          if (subject?.facultyAssignments?.length) {
            facultyIds = subject.facultyAssignments.map(f => f.facultyId);
          }
        }
        
        if (!facultyIds || facultyIds.length === 0) {
          setCheckingConflict(false);
          return;
        }
        
        const conflictResults = await Promise.all(facultyIds.map(async (facultyId) => {
          try {
            const response = await api.post('/api/timetable/check-faculty-conflict', {
              facultyUserId: facultyId,
              dayOfWeek: entry.dayOfWeek,
              timeSlot: entry.timeSlot,
              excludeClassId: classId,
              academicYearId: academicYearId,
              tenantId: tenantId,
              ...(isSemesterBased && semesterId ? { semesterType: semesterId } : {}),
              ...(!isSemesterBased && selectedTermId ? { termId: selectedTermId } : {})
            }, {
              headers: { Authorization: `Bearer ${authToken}` }
            });
            
            if (response.data.conflict && response.data.conflicts?.length > 0) {
              return {
                facultyId,
                facultyName: response.data.facultyName,
                conflicts: response.data.conflicts
              };
            }
            return null;
          } catch (err) {
            console.error('[TimetableEntry] Conflict check failed for faculty:', facultyId, err);
            return null;
          }
        }));
        
        const conflicts = conflictResults.filter(r => r !== null);
        
        if (conflicts.length > 0) {
          setCurrentConflict({
            facultyConflicts: conflicts,
            message: `${conflicts.length} faculty conflict(s) detected`,
            timeSlot: entry.timeSlot,
            dayOfWeek: entry.dayOfWeek
          });
          setShowConflictModal(true);
        }
      } catch (err) {
        console.error('[TimetableEntry] Conflict check failed:', err);
      } finally {
        setCheckingConflict(false);
      }
    }, 800);
    
    setConflictCheckDebounce(timeoutId);
  }, [entry.dayOfWeek, entry.timeSlot, classId, academicYearId, tenantId, isSemesterBased, semesterId, selectedTermId, authToken, safeSubjects, conflictCheckDebounce]);

  const handleSubjectOrGroupChange = useCallback((value) => {
    if (!value) {
      onUpdate(index, 'subjectId', null);
      onUpdate(index, 'electiveGroupId', null);
      onUpdate(index, 'electiveSelections', []);
      setSelectedSubjectFaculty(null);
      return;
    }
    
    if (value.startsWith('group:')) {
      const groupId = value.split(':')[1];
      const group = effectiveElectiveGroups.find(g => String(g._id) === String(groupId));
      if (!group) return;

      const electiveSelections = [];
      for (const gs of group.subjects || []) {
        const hasSubgroups = gs.subGroups && gs.subGroups.length > 0;
        const selection = {
          subjectId: gs.subjectId,
          facultyId: hasSubgroups ? null : gs.facultyId,
          subgroupSelections: []
        };

        if (hasSubgroups) {
          selection.subgroupSelections = gs.subGroups.map(sg => ({
            subGroupId: sg.subGroupId,
            facultyId: sg.facultyId
          }));
        }

        electiveSelections.push(selection);
      }

      onUpdate(index, 'electiveGroupId', groupId);
      onUpdate(index, 'subjectId', null);
      onUpdate(index, 'electiveSelections', electiveSelections);
      setSelectedSubjectFaculty(null);

      // For group, keep existing logic (all faculty in group checked)
      const allFacultyIds = [];
      for (const gs of group.subjects || []) {
        if (gs.facultyId) allFacultyIds.push(String(gs.facultyId));
        for (const sg of (gs.subGroups || [])) {
          if (sg.facultyId) allFacultyIds.push(String(sg.facultyId));
        }
      }
      if (allFacultyIds.length > 0) {
        checkEntryConflict(null, allFacultyIds);
      }
    } else {
      const selectedSubject = safeSubjects.find(s => String(s.id || s._id) === String(value));
      onUpdate(index, 'subjectId', value);
      onUpdate(index, 'electiveGroupId', null);
      onUpdate(index, 'electiveSelections', []);

      if (selectedSubject?.isLab || selectedSubject?.type === 'Lab') {
        onUpdate(index, 'type', 'Lab');
      } else {
        onUpdate(index, 'type', 'Period');
      }

      if (typeof onSubjectChange === 'function') {
        onSubjectChange(index, value, entry.dayOfWeek, entry.timeSlot);
      }

      // Only check conflict for the currently assigned faculty (not all facultyAssignments)
      let facultyIdToCheck = null;
      if (selectedSubject?.facultyAssignments?.length) {
        // Prefer class-specific assignment, else first assignment
        const facultyAssignment = selectedSubject.facultyAssignments.find(f => 
          f.classAssignments?.some(ca => String(ca.classId) === String(classId))
        ) || selectedSubject.facultyAssignments[0];
        facultyIdToCheck = facultyAssignment?.facultyId;
      }
      if (facultyIdToCheck) {
        checkEntryConflict(value, [facultyIdToCheck]);
      }
    }
  }, [effectiveElectiveGroups,classId, safeSubjects, onUpdate, index, onSubjectChange, entry.dayOfWeek, entry.timeSlot, checkEntryConflict]);

  // Handle common break selection - only sets the label, does NOT change time
  const handleCommonBreakSelect = useCallback((breakItem) => {
    onUpdate(index, 'type', 'Break');
    onUpdate(index, 'label', breakItem.label);
  }, [onUpdate, index]);

  const handleSessionRoomChange = useCallback((value) => {
    onUpdate(index, 'room', value);
  }, [onUpdate, index]);

  const handleTimeChange = useCallback((field, value) => {
    onUpdate(index, `timeSlot.${field}`, value);
    if (entry.electiveGroupId) {
      const group = effectiveElectiveGroups.find(g => String(g._id) === String(entry.electiveGroupId));
      if (group) {
        const allFacultyIds = [];
        for (const gs of group.subjects || []) {
          if (gs.facultyId) allFacultyIds.push(String(gs.facultyId));
          for (const sg of (gs.subGroups || [])) {
            if (sg.facultyId) allFacultyIds.push(String(sg.facultyId));
          }
        }
        if (allFacultyIds.length > 0) {
          checkEntryConflict(null, allFacultyIds);
        }
      }
    } else if (entry.subjectId) {
      checkEntryConflict(entry.subjectId);
    }
  }, [onUpdate, index, entry.electiveGroupId, entry.subjectId, effectiveElectiveGroups, checkEntryConflict]);

  const handleRemoveClick = useCallback(() => setShowConfirmModal(true), []);
  const handleConfirmRemove = useCallback(() => {
    setShowConfirmModal(false);
    onRemove(index);
  }, [onRemove, index]);
  const handleCancelRemove = useCallback(() => setShowConfirmModal(false), []);
  const handleCloseConflictModal = useCallback(() => {
    setShowConflictModal(false);
    setCurrentConflict(null);
  }, []);

  const checkIsLab = useCallback((subjectId) => {
    if (!subjectId) return false;
    const subj = safeSubjects.find(s => String(s.id || s._id) === String(subjectId));
    return subj?.isLab === true || subj?.type === 'Lab' || subj?.subjectType === 'Lab';
  }, [safeSubjects]);

  useEffect(() => {
    if (entry.subjectId && !entry.electiveGroupId) {
      const isLab = checkIsLab(entry.subjectId);
      const targetType = isLab ? 'Lab' : 'Period';
      if (entry.type !== targetType) {
        onUpdate(index, 'type', targetType);
      }
    }
  }, [entry.subjectId, entry.electiveGroupId, index, checkIsLab, onUpdate, entry.type]);

  const getSummaryLabel = useCallback(() => {
    if (entry.type === 'Break') return `Break: ${entry.label || 'Untitled'}`;
    if (entry.type === 'Custom') return entry.label || 'Custom Session';
    if (entry.electiveGroupId && selectedElectiveGroup) {
      const subjectsList = selectedElectiveGroup.subjects || [];
      const summary = subjectsList.map(gs => {
        const sub = safeSubjects.find(s => String(s._id || s.id) === String(gs.subjectId));
        const hasSubgroups = gs.subGroups && gs.subGroups.length > 0;
        return `${sub?.name || 'Subject'}${hasSubgroups ? ` (${gs.subGroups.length} groups)` : ''}`;
      }).join(' + ');
      
      return `${selectedElectiveGroup.groupName || 'Program'}: ${summary}`;
    }
    if (entry.subjectId) {
      const subject = safeSubjects.find(s => String(s.id || s._id) === String(entry.subjectId));
      const subjectCode = subject?.subjectCode ? ` [${subject.subjectCode}]` : '';
      const facultyText = selectedSubjectFaculty ? ` - ${selectedSubjectFaculty}` : '';
      return `${subject?.name || 'Unknown Subject'}${subjectCode}${facultyText}`;
    }
    return 'Empty Slot';
  }, [entry.type, entry.label, entry.electiveGroupId, entry.subjectId, selectedElectiveGroup, safeSubjects, selectedSubjectFaculty]);

  const isLabSubject = entry.type === 'Lab' || checkIsLab(entry.subjectId);
  const hasLabBatches = safeLabBatches.length > 0;
  const batchRequired = isLabSubject && hasLabBatches;
  const batchMissing = batchRequired && !entry.batchId;

  // Common Breaks Dropdown Menu
  const commonBreaksMenu = (
    <Menu>
      {COMMON_BREAKS.map((breakItem, idx) => (
        <Menu.Item key={idx} onClick={() => handleCommonBreakSelect(breakItem)}>
          <div className="flex items-center gap-2">
            <span className="text-base">{breakItem.icon}</span>
            <span className="text-sm font-medium">{breakItem.label}</span>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  // Conflict Modal Component
  const ConflictWarningModal = useCallback(() => {
    if (!showConflictModal || !currentConflict) return null;
    
    const { facultyConflicts, message, dayOfWeek, timeSlot } = currentConflict;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
          <div className="bg-yellow-50 border-b border-yellow-200 p-4 rounded-t-lg">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <FaExclamationTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-yellow-800">⚠️ Scheduling Conflicts Detected</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  {dayOfWeek} at {to12Hour(timeSlot.start)} - {to12Hour(timeSlot.end)}
                </p>
                <p className="text-sm text-yellow-700">{message}</p>
              </div>
            </div>
          </div>
          
          <div className="p-4">
            {facultyConflicts.map((conflict, idx) => (
              <div key={idx} className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  <span className="font-bold">{conflict.facultyName || 'Faculty'}</span> is already scheduled in:
                </p>
                <div className="space-y-3">
                  {conflict.conflicts.map((conf, cidx) => (
                    <div key={cidx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0"></div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">
                            {conf.className || 'Another Class'}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            📚 {conf.subject || 'Unknown Subject'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            ⏰ {conf.time || `${timeSlot.start}-${timeSlot.end}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
              <p className="text-xs text-yellow-800">
                ⚠️ This is a warning only. You can still proceed with this selection, but the schedule will have conflicts.
              </p>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 rounded-b-lg flex justify-end gap-3">
            <button
              onClick={handleCloseConflictModal}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Dismiss
            </button>
            <button
              onClick={handleCloseConflictModal}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 transition-colors"
            >
              I Understand, Continue
            </button>
          </div>
        </div>
      </div>
    );
  }, [showConflictModal, currentConflict, handleCloseConflictModal]);

  const renderGroupDetails = () => {
    if (!selectedElectiveGroup) return null;
    
    const subjectsList = selectedElectiveGroup.subjects || [];
    
    return (
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaUsers className="text-gray-500" />
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Program Details</span>
          </div>
          <Badge count={subjectsList.length} className="bg-gray-200 text-gray-600" />
        </div>
        
        {loadingGroupData ? (
          <div className="p-4 text-center">
            <Spin size="small" />
            <p className="text-xs text-gray-400 mt-2">Loading faculty information...</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {subjectsList.map((gs, idx) => {
              const subject = safeSubjects.find(s => String(s._id || s.id) === String(gs.subjectId));
              const hasSubgroups = gs.subGroups && gs.subGroups.length > 0;
              const subjectName = subject?.name || subject?.subjectName || 'Unknown Subject';
              const subjectCode = subject?.subjectCode ? ` [${subject.subjectCode}]` : '';
              
              return (
                <div key={idx} className="p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800">{subjectName}{subjectCode}</span>
                        {hasSubgroups && (
                          <Badge count={`${gs.subGroups.length} groups`} className="bg-blue-100 text-blue-600 text-[10px]" />
                        )}
                      </div>
                      
                      <div className="mt-2 space-y-1">
                        {hasSubgroups ? (
                          gs.subGroups.map((sg, sgIdx) => {
                            const facultyName = groupFacultyNames[`${gs.subjectId}_${sg.subGroupId}`] || 
                                              groupFacultyNames[sg.facultyId] || 
                                              'Loading...';
                            return (
                              <div key={sg.subGroupId} className="flex items-center gap-2 text-xs text-gray-600 pl-2 border-l-2 border-gray-200">
                                <FaUsers className="text-gray-400" size={10} />
                                <span>Group {sgIdx + 1}:</span>
                                <span className="font-medium">{facultyName}</span>
                                {sg.room && <span className="text-gray-400">• Room {sg.room}</span>}
                              </div>
                            );
                          })
                        ) : (
                          <div className="flex items-center gap-2 text-xs text-gray-600 pl-2">
                            <FaChalkboardTeacher className="text-gray-400" size={10} />
                            <span>Faculty:</span>
                            <span className="font-medium">
                              {groupFacultyNames[gs.subjectId] || groupFacultyNames[gs.facultyId] || 'No faculty assigned'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Custom filter function for Select - simplified to prevent freezing
  const filterOption = useCallback((input, option) => {
    if (!input) return true;
    const searchTerm = input.toLowerCase();
    const value = option.value;
    
    // Check regular subjects
    const regularMatch = dropdownOptions.regularOptions.find(opt => opt.value === value);
    if (regularMatch) {
      return regularMatch.searchText.includes(searchTerm);
    }
    
    // Check groups
    const groupMatch = dropdownOptions.groupOptions.find(opt => opt.value === value);
    if (groupMatch) {
      return groupMatch.searchText.includes(searchTerm);
    }
    
    return false;
  }, [dropdownOptions]);

  return (
    <>
      <style>{animationStyles}</style>
      
      <ConflictWarningModal />
      
      <div className={`relative bg-white rounded-lg border transition-all duration-200 mb-2 shadow-sm
        ${isExpanded ? 'border-blue-400 ring-1 ring-blue-50 shadow-md z-10' : 'border-gray-200 hover:border-blue-300'}
      `}>
        {currentConflict && !showConflictModal && (
          <div className="absolute top-0 right-0 m-2 px-2 py-1 bg-yellow-100 border border-yellow-300 text-xs text-yellow-700 rounded shadow z-20 flex items-center gap-1">
            <FaExclamationTriangle size={10} />
            <span>Conflict detected</span>
          </div>
        )}

        {/* HEADER */}
        <div 
          className="flex items-center justify-between p-3 cursor-pointer bg-white rounded-lg hover:bg-gray-50 min-h-[52px]"
          onClick={() => !disableEditing && setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center space-x-3 overflow-hidden flex-1">
            <div className={`w-1.5 h-10 rounded-full flex-shrink-0 ${
              entry.type === 'Break' ? 'bg-orange-400' : 
              entry.type === 'Lab' ? 'bg-purple-500' : 
              entry.electiveGroupId ? 'bg-indigo-500' :
              'bg-blue-500'
            }`}></div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-gray-500 font-mono">
                  {to12Hour(entry.timeSlot.start)} - {to12Hour(entry.timeSlot.end)}
                </span>
                
                {entry.electiveGroupId && (
                  <Badge color="purple" className="text-[10px]">PROGRAM</Badge>
                )}
                
                {entry.type === 'Lab' && (
                  <Badge color="purple" className="text-[10px]">LAB</Badge>
                )}
                
                {entry.type === 'Break' && (
                  <Badge color="orange" className="text-[10px]">BREAK</Badge>
                )}
              </div>
              
              <div className="font-medium text-sm text-gray-800 truncate" title={getSummaryLabel()}>
                {getSummaryLabel()}
              </div>
              
              {!isExpanded && entry.room && (
                <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                  <MdMeetingRoom size={10} />
                  <span>{entry.room}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2 flex-shrink-0">
            {checkingConflict && (
              <div className="animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-yellow-500"></div>
            )}
            <div className="text-gray-400 p-1 hover:text-gray-600">
              {isExpanded ? <FaChevronUp size={12} /> : <FaEdit size={12} />}
            </div>
          </div>
        </div>

        {/* EXPANDED FORM */}
        {isExpanded && (
          <div className="p-4 border-t border-gray-100 bg-gray-50/50 rounded-b-lg flex flex-col gap-3">
            {/* Type Selection with Common Breaks Dropdown */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 tracking-wider">Session Type</label>
              <div className="flex gap-2">
                <select
                  value={entry.type}
                  onChange={e => onUpdate(index, 'type', e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                  disabled={disableEditing}
                >
                  <option value="Period">📖 Period</option>
                  <option value="Lab">🧪 Lab / Practical</option>
                  <option value="Break">☕ Break</option>
                  <option value="Custom">⚙️ Custom</option>
                </select>
                
                {entry.type === 'Break' && (
                  <Dropdown overlay={commonBreaksMenu} trigger={['click']} disabled={disableEditing}>
                    <button className="px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1">
                      <FaClock size={12} />
                      <span>Quick</span>
                    </button>
                  </Dropdown>
                )}
              </div>
            </div>

            {/* Time Slots */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 tracking-wider">Start Time</label>
                <input
                  type="time"
                  value={entry.timeSlot.start}
                  onChange={e => handleTimeChange('start', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  disabled={disableEditing}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 tracking-wider">End Time</label>
                <input
                  type="time"
                  value={entry.timeSlot.end}
                  onChange={e => handleTimeChange('end', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  disabled={disableEditing}
                />
              </div>
            </div>

            {/* Break/Custom Label */}
            {(entry.type === 'Break' || entry.type === 'Custom') && (
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 tracking-wider">
                  {entry.type === 'Break' ? 'Break Name' : 'Session Name'}
                </label>
                <input
                  type="text"
                  placeholder={entry.type === 'Break' ? 'e.g., Short Break' : 'e.g., Assembly'}
                  value={entry.label || ''}
                  onChange={e => onUpdate(index, 'label', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  disabled={disableEditing}
                />
              </div>
            )}

            {/* Subject/Group Selection */}
            {(entry.type === 'Period' || entry.type === 'Lab') && (
              <>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 tracking-wider">
                    {entry.type === 'Lab' ? 'Subject / Lab' : 'Subject / Program'}
                  </label>
                  <Select
                    showSearch
                    placeholder="Search by name or code..."
                    value={entry.electiveGroupId ? `group:${entry.electiveGroupId}` : (entry.subjectId || undefined)}
                    onChange={handleSubjectOrGroupChange}
                    filterOption={filterOption}
                    style={{ width: '100%' }}
                    size="middle"
                    dropdownStyle={{ minWidth: 280, maxWidth: 400 }}
                    disabled={disableEditing}
                    notFoundContent="No matching subjects or programs found"
                  >
                    {dropdownOptions.regularOptions.length > 0 && (
                      <Select.OptGroup label="📚 Regular Subjects">
                        {dropdownOptions.regularOptions.map(opt => (
                          <Select.Option key={opt.value} value={opt.value}>
                            <div className="flex items-center justify-between">
                              <span>{opt.label}</span>
                              {opt.isLab && (
                                <Badge color="purple" className="text-[9px] ml-2">Lab</Badge>
                              )}
                            </div>
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    )}
                    
                    {dropdownOptions.groupOptions.length > 0 && (
                      <Select.OptGroup label="📋 Academic Programs">
                        {dropdownOptions.groupOptions.map(opt => (
                          <Select.Option key={opt.value} value={opt.value}>
                            <div className="flex flex-col">
                              <span className="font-medium">{opt.label}</span>
                              {opt.description && (
                                <span className="text-[10px] text-gray-400 truncate">{opt.description}</span>
                              )}
                            </div>
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    )}
                  </Select>
                </div>

                {/* Faculty Display for Regular Subjects */}
                {/* Show faculty for regular subject, or for lab subject only after batch is selected */}
                {/* DEBUG: Log faculty display condition */}
                {(() => {
                  if (entry.subjectId && selectedSubjectFaculty && !entry.electiveGroupId) {
                    console.log('[TimetableEntry] Displaying faculty:', selectedSubjectFaculty);
                    return (
                      <div className="flex items-center p-3 bg-blue-50 border border-blue-100 rounded-lg animate-faculty-reveal">
                        <div className="text-blue-500 mr-3">
                          <FaChalkboardTeacher size={14} />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[9px] text-blue-400 uppercase font-bold tracking-wider">Assigned Faculty</span>
                          <span className="text-sm font-semibold text-gray-700">{selectedSubjectFaculty}</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}



                {/* Room */}
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 tracking-wider">
                    Room <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <MdMeetingRoom />
                    </div>
                    <input
                      type="text"
                      placeholder="Room number or name"
                      value={entry.room || ''}
                      onChange={(e) => handleSessionRoomChange(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      disabled={disableEditing}
                    />
                  </div>
                </div>

                {/* Group Details */}
                {selectedElectiveGroup && renderGroupDetails()}

                {/* Batch Selection for Labs */}
                {batchRequired && (
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 tracking-wider">
                      Batch <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <FaFlask />
                      </div>
                      <select
                        value={entry.batchId || ''}
                        onChange={e => onUpdate(index, 'batchId', e.target.value || null)}
                        className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white ${
                          batchMissing ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-300'
                        }`}
                        disabled={disableEditing}
                      >
                        <option value="">Select a batch</option>
                        {safeLabBatches.map(b => {
                          const id = b._id || b.id || b.batchId;
                          const label = b.name || b.batchName || (b.batchNumber ? `Batch ${b.batchNumber}` : 'Unnamed Batch');
                          return <option key={String(id)} value={String(id)}>{label}</option>;
                        })}
                      </select>
                    </div>
                    {batchMissing && (
                      <div className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <FaExclamationTriangle size={10} />
                        <span>Batch selection is required for lab subjects</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Footer Actions */}
            <div className="flex justify-end pt-3 border-t border-gray-200 mt-2">
              <button
                onClick={handleRemoveClick}
                className="flex items-center px-3 py-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg text-sm transition-colors"
                disabled={disableEditing}
              >
                <FaTrash className="mr-2" size={12} />
                Delete Entry
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showConfirmModal && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 rounded-lg border border-gray-100 backdrop-blur-[1px]">
            <div className="text-center w-full px-4">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Remove this entry?</h3>
              <div className="flex gap-3 justify-center">
                <button onClick={handleCancelRemove} className="px-4 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium">Cancel</button>
                <button onClick={handleConfirmRemove} className="px-4 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 text-sm font-medium shadow-sm">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TimetableEntry;